import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { useGetAvatarsQuery, useUpdateAvatarImageMutation } from "../../../../../../service/user-service";
import useWindowDimensions from "../../../../../../hooks/use-window-dimensions";
import styles from "./avatar-modal.module.scss";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../../utils/error-handler";

interface AvatarModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: () => void;
}

export const AvatarModal = ({ isOpen, onClose, onSave }: AvatarModalProps) => {
	const { t } = useTranslation();
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const { data: avatars, isLoading } = useGetAvatarsQuery();
	const [updateAvatar, { isLoading: isSaving }] = useUpdateAvatarImageMutation();

	if (!isOpen) return null;

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleSave = async () => {
		if (!selectedId) return;
		updateAvatar({ avatarId: selectedId }).unwrap().then(()=> {
			toast.success(t("account.web.profileImageUpdateSuccessful"))
			onSave();
		}).catch(err => {
			toast.error(getErrorMessage(err?.data))
		})
	};

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<h2 className={styles.title}>{t("account.chooseYourAvatar")}</h2>
					<button className={styles.closeButton} onClick={onClose}>
						×
					</button>
				</div>
				<div className={styles.gridContainer}> 
				<div className={styles.grid}>
					{isLoading
						? Array.from({ length: 12 }).map((_, i) => (
								<div key={i} className={styles.skeletonItem} />
						  ))
						: avatars?.map(avatar => (
								<button
									key={avatar.id}
									className={`${styles.avatarItem} ${selectedId === avatar.id ? styles.selected : ""}`}
									onClick={() => setSelectedId(avatar.id)}
								>
									<img src={avatar.imageUrl} alt={t("account.web.avatarOptionAlt")} className={styles.avatarImage} />
								</button>
						  ))}
				</div>
				</div>

				<div className={styles.footer}>
					<Button variant="primary" fullWidth disabled={!selectedId || isSaving} onClick={handleSave}>
						{isSaving ? t("account.web.saving") : t("common.actions.saveKey")}
					</Button>
				</div>
			</div>
		</div>
	);
};